// @givmo/board — what a copy of the minute book says about the closed records it left out and opened
// (CBG-RC12-EXPORT-WEB; backend `agent_docs/domain/board_minutes.md` §O9 and §O9a).
//
// Both copies say it, each in its own place. The machine-readable copy says it in its body, beside
// every hash: `not_in_this_export`, `exporter_is_a_current_director` and `opened_for_this_export`.
// The readable copy's body IS the document, so it says it in a response header,
// `X-Givmo-Board-Export-Closed-Records`, the same three members read off the manifest the document was
// rendered from. Either way the dialog after a download describes the file that was saved, in one
// read.
//
// ⚠ THE HEADER IS READ TRI-STATE (`agent_docs/api-client.md`, "Reading a response HEADER"). Absent
// means nothing reported: the copy left nothing out and opened nothing, or the backend predates the
// header, or the list passed the header's bound (the document still prints its own section). The page
// then says nothing, as before. An unreadable value reads the same. Nothing is inferred from absence.

import type { OrgBoardOrgExportBoardMinuteBookResponse } from '@givmo/api-client';

/** The readable copy's header. The backend exposes it to the consoles (`CORS_EXPOSE_HEADERS`). */
export const CLOSED_RECORDS_HEADER = 'X-Givmo-Board-Export-Closed-Records';

/** One closed session a copy left out, by its public facts only: the meeting, its day, its purpose. */
export type LeftOutSession = NonNullable<
  OrgBoardOrgExportBoardMinuteBookResponse['not_in_this_export']
>[number];

/** What a copy said about the closed records it left out and opened. */
export interface CopyClosedRecords {
  /** The closed sessions' own minutes the copy left out, in the copy's order. */
  leftOut: LeftOutSession[];
  /** True only when the copy said its exporter is a current director, who may include them. */
  exporterIsACurrentDirector: boolean;
  /** The closed records this copy opened, because a current director asked to include them. */
  opened: string[];
}

function record(value: unknown): Record<string, unknown> | null {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function text(value: unknown): string | null {
  return typeof value === 'string' && value.length > 0 ? value : null;
}

function leftOutOf(value: unknown): LeftOutSession[] {
  if (!Array.isArray(value)) return [];
  return value
    .map((entry): LeftOutSession | null => {
      const row = record(entry);
      const meetingId = text(row?.meeting_id);
      const title = text(row?.meeting_title);
      if (row === null || meetingId === null || title === null) return null;
      return {
        meeting_id: meetingId,
        meeting_title: title,
        held_on: text(row.held_on),
        purpose_label: text(row.purpose_label),
      };
    })
    .filter((entry): entry is LeftOutSession => entry !== null);
}

function closedRecordsOf(value: Record<string, unknown>): CopyClosedRecords {
  return {
    leftOut: leftOutOf(value.not_in_this_export),
    exporterIsACurrentDirector: value.exporter_is_a_current_director === true,
    opened: Array.isArray(value.opened_for_this_export)
      ? value.opened_for_this_export.filter((id): id is string => typeof id === 'string')
      : [],
  };
}

/** What the machine-readable copy says about its closed records, from its own body. */
export function closedRecordsOfManifest(
  manifest: OrgBoardOrgExportBoardMinuteBookResponse,
): CopyClosedRecords {
  return closedRecordsOf((record(manifest) ?? {}) as Record<string, unknown>);
}

/** What the readable copy says, from its header's value; null when it reported nothing readable. */
export function decodeClosedRecordsHeader(
  value: string | null | undefined,
): CopyClosedRecords | null {
  if (typeof value !== 'string' || value.trim().length === 0) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(value);
  } catch {
    return null;
  }
  const row = record(parsed);
  return row === null ? null : closedRecordsOf(row);
}
